import { DateTime } from 'luxon'

// Get list of IANA timezones supported by the environment
const timezones =
  typeof (Intl as any).supportedValuesOf === 'function' ? (Intl as any).supportedValuesOf('timeZone') : [] // fallback to empty or static list
const timeZoneMap: Record<string, string> = {}

const timezonesWithOffset = timezones.map((tz: string) => {
  // Make sure to call the function correctly (no missing parentheses)
  const currentTime = DateTime.now().setZone(tz)
  const offsetInMinutes = currentTime.offset // offset in minutes
  const offsetInHours = offsetInMinutes / 60

  return {
    timezone: tz,
    offset: offsetInHours,
  }
})

// Sort the array by offset (UTC offset)
timezonesWithOffset.sort((a: any, b: any) => {
  if (a.offset === b.offset) {
    return a.timezone.localeCompare(b.timezone) // Sort alphabetically if offsets are equal
  }
  return a.offset - b.offset // Sort by offset
})

timezonesWithOffset.map((tz: any) => {
  timeZoneMap[tz.timezone] = `${tz.timezone.replaceAll('_', ' ').replace('/', ', ')} (UTC${tz.offset >= 0 ? '+' : ''}${
    tz.offset
  }:00)`
})

export default timeZoneMap

/**
 * Which timezone the app renders timestamps in, resolved per viewer.
 *
 * Three layers, most specific wins:
 *   1. the signed-in user's `configuration.timezone` (Profile -> Configurations)
 *   2. the company default, `globalSettingV2.defaultTimezone` (Admin -> Global Settings)
 *   3. DEFAULT_TIME_ZONE - what every timestamp rendered before this feature used
 *
 * `formatTime`/`formatDate` in `utils/convertor` read this, which is what carries the
 * setting to the ~360 call sites that display a date. Note what does NOT move with it:
 * a business date stored as a wall-clock 'YYYY-MM-DD' string (maturity, closing, due
 * dates) formats to the same day in any zone, by construction, and rate-lock expiry
 * stays on the company's secondary-marketing business hours.
 */
export const DEFAULT_TIME_ZONE = 'America/New_York'

const STORAGE_KEY = 'nexys.timeZone'

let companyTimeZone = ''
let userTimeZone = ''

/** True when the value is an IANA zone this browser actually knows. */
export const isValidTimeZone = (value?: string | null): value is string => {
  if (!value || typeof value !== 'string') return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value })
    return true
  } catch {
    return false
  }
}

/** The zone a timestamp shown to this viewer should be rendered in. */
export const getActiveTimeZone = (): string => userTimeZone || companyTimeZone || DEFAULT_TIME_ZONE

/** Company-wide default, from `globalSettingV2.defaultTimezone` at app init. */
export const setCompanyTimeZone = (value?: string | null) => {
  companyTimeZone = isValidTimeZone(value) ? value : ''
}

/**
 * The signed-in user's choice. Remembered in localStorage so the very first render
 * after a reload already uses it, instead of flashing the default zone while the
 * profile loads.
 */
export const setUserTimeZone = (value?: string | null) => {
  userTimeZone = isValidTimeZone(value) ? value : ''
  try {
    if (userTimeZone) localStorage.setItem(STORAGE_KEY, userTimeZone)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // localStorage unavailable (private mode) - the zone still applies this session.
  }
}

/** The last-applied user zone, read synchronously at boot. */
export const getStoredTimeZone = (): string => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isValidTimeZone(stored)) return stored
  } catch {
    // ignore
  }
  return ''
}
